/**
 * useFeatureAccess Hook - FIX 3
 * 
 * Provides feature gating based on user's current plan.
 * Works with PLAN_FEATURES config from backend for consistency.
 */

import { useMemo, useCallback } from 'react';
import { usePlan } from '../contexts/PlanContext';

// Mirror of backend PLAN_FEATURES for client-side checks
// This MUST stay in sync with backend/plan_features.py
export const PLAN_FEATURES = {
  free: {
    id: "free",
    name: "Free",
    words_per_month: 500,
    features: {
      translation: { enabled: true, limit: 500 },
      mtpe: { enabled: false },
      mtpe_advanced: { enabled: false },
      cat: { enabled: false },
      tqa: { enabled: false, limit: 0 },
      tm: { enabled: false },
      glossary: { enabled: false },
      batch: { enabled: false },
      review: { enabled: false },
      team: { enabled: false, limit: 1 },
      api: { enabled: false },
    }
  },
  payg: {
    id: "payg",
    name: "Pay As You Go",
    words_per_month: null,
    features: {
      translation: { enabled: true, limit: null },
      mtpe: { enabled: true, limit: null },
      mtpe_advanced: { enabled: true, limit: null },
      cat: { enabled: true, limit: null },
      tqa: { enabled: true, limit: null },
      tm: { enabled: true },
      glossary: { enabled: true },
      batch: { enabled: true },
      review: { enabled: true },
      team: { enabled: false, limit: 1 },
      api: { enabled: false },
    }
  },
  starter: {
    id: "starter",
    name: "Starter",
    words_per_month: 20000,
    features: {
      translation: { enabled: true, limit: 20000 },
      mtpe: { enabled: true, limit: 20000 },
      mtpe_advanced: { enabled: false },
      cat: { enabled: false },
      tqa: { enabled: false, limit: 0 },
      tm: { enabled: true },
      glossary: { enabled: true },
      batch: { enabled: true },
      review: { enabled: false },
      team: { enabled: false, limit: 1 },
      api: { enabled: false },
    }
  },
  professional: {
    id: "professional",
    name: "Professional",
    words_per_month: 60000,
    is_popular: true,
    features: {
      translation: { enabled: true, limit: 60000 },
      mtpe: { enabled: true, limit: 60000 },
      mtpe_advanced: { enabled: true, limit: 60000 },
      cat: { enabled: true, limit: 60000 },
      tqa: { enabled: true, limit: 5 },
      tm: { enabled: true },
      glossary: { enabled: true },
      batch: { enabled: true },
      review: { enabled: true },
      team: { enabled: false, limit: 1 },
      api: { enabled: false },
    }
  },
  business: {
    id: "business",
    name: "Business",
    words_per_month: 200000,
    features: {
      translation: { enabled: true, limit: 200000 },
      mtpe: { enabled: true, limit: 200000 },
      mtpe_advanced: { enabled: true, limit: 200000 },
      cat: { enabled: true, limit: 200000 },
      tqa: { enabled: true, limit: -1 }, // Unlimited
      tm: { enabled: true },
      glossary: { enabled: true },
      batch: { enabled: true },
      review: { enabled: true },
      team: { enabled: true, limit: 3 },
      api: { enabled: false },
    }
  }
};

// Feature display names
export const FEATURE_DISPLAY_NAMES = {
  translation: "Basic Translation",
  mtpe: "MTPE",
  mtpe_advanced: "MTPE Pro",
  cat: "CAT Tool",
  tqa: "TQA Reports",
  tm: "Translation Memory",
  glossary: "Glossary",
  batch: "Batch Processing",
  review: "Review & Proofread",
  team: "Team Collaboration",
  api: "API Access"
};

/**
 * Find the minimum plan required for a feature
 */
const findMinimumPlanForFeature = (featureName) => {
  const planOrder = ["free", "payg", "starter", "professional", "business"];
  
  for (const planId of planOrder) {
    const planConfig = PLAN_FEATURES[planId];
    const featureConfig = planConfig?.features?.[featureName];
    if (featureConfig?.enabled) {
      return planId;
    }
  }
  return null;
};

/**
 * Main hook for feature access checking
 * 
 * Usage:
 *   const { hasAccess, limit, upgradeTo, checkFeature } = useFeatureAccess('cat');
 *   
 *   if (!hasAccess) {
 *     return <UpgradePrompt feature="cat" upgradeTo={upgradeTo} />;
 *   }
 */
export const useFeatureAccess = (featureName) => {
  const { planInfo, user } = usePlan();
  
  const result = useMemo(() => {
    const userPlan = planInfo?.plan || user?.plan || 'free';
    const planConfig = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
    const featureConfig = planConfig.features?.[featureName] || { enabled: false };
    
    const hasAccess = featureConfig.enabled === true;
    const limit = featureConfig.limit;
    const upgradeTo = hasAccess ? null : findMinimumPlanForFeature(featureName);
    
    return {
      hasAccess,
      limit,
      upgradeTo,
      upgradePlanName: upgradeTo ? PLAN_FEATURES[upgradeTo]?.name : null,
      featureName,
      featureDisplayName: FEATURE_DISPLAY_NAMES[featureName] || featureName,
      currentPlan: userPlan,
      currentPlanName: planConfig.name
    };
  }, [planInfo, user, featureName]);
  
  return result;
};

/**
 * Hook to get all feature statuses at once
 */
export const useAllFeatureAccess = () => {
  const { planInfo, user } = usePlan();
  
  return useMemo(() => {
    const userPlan = planInfo?.plan || user?.plan || 'free';
    const planConfig = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
    
    const features = {};
    for (const [featureName, featureConfig] of Object.entries(planConfig.features || {})) {
      features[featureName] = {
        enabled: featureConfig.enabled === true,
        limit: featureConfig.limit,
        displayName: FEATURE_DISPLAY_NAMES[featureName] || featureName,
        upgradeTo: featureConfig.enabled ? null : findMinimumPlanForFeature(featureName)
      };
    }
    
    return {
      features,
      currentPlan: userPlan,
      currentPlanName: planConfig.name,
      wordsPerMonth: planConfig.words_per_month
    };
  }, [planInfo, user]);
};

/**
 * Hook for checking multiple features
 */
export const useFeatureCheck = () => {
  const { planInfo, user } = usePlan();
  
  const checkFeature = useCallback((featureName) => {
    const userPlan = planInfo?.plan || user?.plan || 'free';
    const planConfig = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
    const featureConfig = planConfig.features?.[featureName] || { enabled: false };
    
    return {
      hasAccess: featureConfig.enabled === true,
      limit: featureConfig.limit,
      upgradeTo: featureConfig.enabled ? null : findMinimumPlanForFeature(featureName)
    };
  }, [planInfo, user]);
  
  return { checkFeature };
};

export default useFeatureAccess;
